import { pickBy, uniqBy } from "~/helpers/object-utility.js";
import { ShoppingCartItemEnum } from "~/components/ecommerce/types/ShoppingCartItemEnum";
import { ProductCategory } from "~/components/ecommerce/types/ProductCategory";
import { v4 as uuid } from "uuid";

export const isPhone = (item) =>
	item?.type === "hardware" && [ProductCategory.PHONE, ProductCategory.GOOD_AS_NEW].includes(item?.metadata?.category);

export const isDevice = (item) =>
	[ProductCategory.PHONE, ProductCategory.GOOD_AS_NEW, ProductCategory.WATCH, ProductCategory.TABLET].includes(
		item?.metadata?.category || item?.category,
	);

export const productIsEligibleForDownPayment = (item) =>
	isCategoryEligibleForDownPayment(item?.metadata?.category || item?.category || item?.name);

export const isCategoryEligibleForDownPayment = (category) => {
	const categoriesThatCanOrderDownPayment = [
		ProductCategory.PHONE,
		ProductCategory.GOOD_AS_NEW,
		ProductCategory.WATCH,
		ProductCategory.TABLET,
	];
	return categoriesThatCanOrderDownPayment.includes(category);
};

const isEligibleForSubscription = (item) => {
	const canHaveSubscription = [
		ProductCategory.PHONE,
		ProductCategory.GOOD_AS_NEW,
		ProductCategory.WATCH,
		ProductCategory.TABLET,
		"mobil", // TODO: Can probably be removed
	];

	const category = item?.metadata?.category || item?.category || item?.name;
	return canHaveSubscription.includes(category);
};

export const getValidatedSubscriptionPrice = (subscriptions, subscriptionItems) => {
	return subscriptions?.find(
		(subscription) =>
			subscription?.msisdn ===
			subscriptionItems
				?.find((item) => item.type === "msisdn")
				?.metadata?.msisdnDetails?.msisdn?.replace(/^47(.{8,})$/, "$1"),
	)?.newPriceFull;
};

const getMetadataPrice = (terminal, isVatExempt = false) => {
	const memberPrice = terminal.metadata?.memberPrice?.prices ? terminal.metadata?.memberPrice : undefined;
	const hasSubscription = terminal.subItems?.some((subItem) => subItem.type === "subscription");

	if (isVatExempt) {
		if (memberPrice !== undefined && hasSubscription) {
			return memberPrice.prices.withoutVat.memberPrice;
		} else return terminal?.metadata?.salePriceWithoutVat || terminal?.metadata?.priceWithoutVat;
	} else {
		if (memberPrice !== undefined && hasSubscription) {
			return memberPrice.prices?.withVat?.memberPrice;
		} else return terminal?.metadata?.salePrice || terminal?.metadata?.price;
	}
};

const getStandaloneProducts = (terminals, sendingTerminalOnSubscriptionOrder) => {
	let standaloneProducts = [];
	const isVatExempt = !!terminals.find((x) => x.type === "hardware")?.metadata?.isVatExempt;

	for (const item of terminals) {
		const product = {
			hardwareProductId: item?.data?.id,
			price: getMetadataPrice(item, isVatExempt),
			quantity: 1,
		};
		if (!sendingTerminalOnSubscriptionOrder && isEligibleForSubscription(item)) {
			standaloneProducts.push(product);
		} else if (isAccessory(item)) {
			const existingProductIndex = standaloneProducts.findIndex(
				(standaloneProduct) => standaloneProduct.hardwareProductId === item?.data?.id,
			);
			const existingItem = standaloneProducts[existingProductIndex];
			if (existingProductIndex === -1) standaloneProducts.push(product);
			else {
				standaloneProducts[existingProductIndex] = { ...existingItem, quantity: existingItem?.quantity + 1 };
			}
		}
		for (const subItem of uniqBy(item.subItems, "data.id")) {
			if (isAccessory(subItem)) {
				if (subItem?.metadata?.preConfigId !== undefined) {
					standaloneProducts.push({
						hardwareProductId: subItem.metadata.preConfigId,
						price: 0.01,
						quantity: 1,
					});
				}
				standaloneProducts.push({
					hardwareProductId: subItem.data.id,
					price: getMetadataPrice(subItem, isVatExempt),
					quantity: item?.subItems?.filter((item) => item?.data?.id === subItem?.data?.id)?.length,
				});
			}
		}
	}
	return standaloneProducts;
};

const getDevices = (terminals) => {
	const hardwareTerminals = terminals?.filter((item) => item?.type === "hardware");
	const tradeIn = terminals?.find((item) => item?.type === ShoppingCartItemEnum.TRADE_IN);
	const isVatExempt = hardwareTerminals?.find((hwTerm) => !!hwTerm?.metadata?.deliveryDetails?.postalCode).metadata
		?.isVatExempt;

	const getHardwareObject = (hardwareItem) => {
		return pickBy({
			hardwareProductId: hardwareItem?.data?.id,
			insuranceProductId: hardwareItem?.subItems?.find((item) => item?.type === "agreement")?.data?.id,
			downPaymentProductId: hardwareItem?.metadata?.downPaymentPlan?.productId,
			numberOfMonthlyInstallments: hardwareItem?.metadata?.downPaymentPlan?.monthlyInstallments,
			price: getMetadataPrice(hardwareItem, isVatExempt),
			...getDeviceReturnIds(hardwareItem),
			...getAmountUpFront(hardwareItem),
		});
	};

	const getDeviceReturnIds = (hardwareItem) => {
		return tradeIn?.metaData?.gradingData?.deviceReturnId &&
			hardwareItem?.metadata?.downPayment &&
			!tradeIn?.metaData?.hasSwap
			? { deviceReturnIds: [tradeIn?.metaData?.gradingData?.deviceReturnId] }
			: undefined;
	};

	const getAmountUpFront = (hardwareItem) => {
		return hardwareItem?.metadata?.downPayment
			? { upfrontPaymentAmount: hardwareItem?.metadata?.downPaymentPlan?.amountUpFront ?? 0 }
			: undefined;
	};

	// partially hardcoded return object
	const getSwapOrderObject = (swapOrderId, isSwap) => {
		if (!swapOrderId) {
			return undefined;
		}

		const isProductionEnvironment = !(
			window.$nuxt?.$store?.state?.host?.split("telenor.no")[0]?.indexOf("test") > -1 ||
			window.$nuxt?.$store?.state?.host?.indexOf("localhost") > -1 ||
			window.location?.hostname?.indexOf("test") > -1 ||
			window.location?.hostname?.indexOf("localhost") > -1 ||
			window.location?.hostname?.indexOf("pippin") > -1
		);

		const swapProductInProduction = {
			swapOrderId,
			hardwareProductId: "646b24274cedfd0001529ba6",
			price: 1,
		};

		const swapProductInTest = {
			swapOrderId,
			hardwareProductId: "6391aea4cff47e0001fa270d",
			price: 1,
		};

		const tradeInProductInProduction = {
			swapOrderId,
			hardwareProductId: "678a0a061c788527f8df78de",
			price: 1,
		};

		const tradeInProductInTest = {
			swapOrderId,
			hardwareProductId: "678e0ab144089a49f1ee6f9a",
			price: 1,
		};

		const swapProduct = isProductionEnvironment ? swapProductInProduction : swapProductInTest;

		const tradeInProduct = isProductionEnvironment ? tradeInProductInProduction : tradeInProductInTest;

		return isSwap ? swapProduct : tradeInProduct;
	};

	return hardwareTerminals.reduce(
		(acc, curr) =>
			[
				...acc,
				getHardwareObject(curr),
				getSwapOrderObject(
					tradeIn?.metaData?.deviceOrderId || tradeIn?.metaData?.existingSwapOrderId,
					tradeIn?.metaData?.hasSwap,
				),
			].filter((x) => x),
		[],
	);
};

const getCustomerReferences = (terminals) => {
	const msisdnDetails = terminals?.find((x) => x.type === "hardware")?.subItems?.find((item) => item?.type === "msisdn")
		?.metadata?.msisdnDetails;
	const kurtId = msisdnDetails?.user?.kurtId || msisdnDetails?.user?.customerIds?.kurtId || msisdnDetails?.user?.kurt;
	return kurtId ? { kurtIdUser: `${kurtId}` } : undefined;
};

const getNewSubscriptionDataStructure = (terminals) => {
	const subscriptionProductId = getSubscriptionProductId(terminals);

	if (!(isOrderingNewSubscription(terminals) && subscriptionProductId)) {
		return undefined;
	} else if (subscriptionProductId?.length > 30) {
		// treating the symptoms - should actually be a change order where exisiting subscription is kept.
		return undefined;
	}

	const msisdnDetails = terminals?.find((x) => x.type === "hardware")?.subItems?.find((item) => item?.type === "msisdn")
		?.metadata?.msisdnDetails;
	const msisdn = removeCountryCode(msisdnDetails?.msisdn);
	const customerReferences = getCustomerReferences(terminals);
	const mainSimcardProduct = { formFactor: "MULTI_SIM", simcardDeliveryAddressType: "USER_RESIDENCE" };
	return [
		pickBy({
			msisdn,
			subscriptionProductId,
			customerReferences,
			devices: getDevices(terminals),
			mainSimcardProduct,
			waitForEquipment: true,
		}),
	];
};

const getSubscriptionProductId = (terminals) =>
	terminals?.find((x) => x.type === "hardware")?.subItems?.find((item) => item?.type === "subscription")?.data?.id;

const getImportSubscriptionDataStructure = (terminals) => {
	const subscriptionProductId = getSubscriptionProductId(terminals);

	if (!(isImportingSubscription(terminals) && subscriptionProductId)) {
		return undefined;
	}

	const msisdnDetails = terminals?.find((x) => x.type === "hardware")?.subItems?.find((item) => item?.type === "msisdn")
		?.metadata?.msisdnDetails;
	const msisdn = removeCountryCode(msisdnDetails?.msisdn);
	const customerReferences = getCustomerReferences(terminals);
	const mainSimcardProduct = { formFactor: "MULTI_SIM", simcardDeliveryAddressType: "USER_RESIDENCE" };
	return [
		pickBy({
			subscriptionProductId,
			customerReferences,
			importInfo: {
				importDateAndTime: new Date(),
				autoCorrectImportDateAndTimeIfInvalid: true,
			},
			devices: getDevices(terminals),
			mainSimcardProduct,
			msisdn,
			waitForEquipment: true,
		}),
	];
};

const removeCountryCode = (number) => {
	if (!number) return undefined;
	if (number?.substring(0, 4) === "0047") {
		number = number.substring(4);
	}
	if (number?.substring(0, 3) === "+47") {
		number = number.substring(3);
	}
	if (number?.substring(0, 2) === "47" && number.length === 10) {
		number = number.substring(2);
	}
	return number;
};

const getChangeSubscriptionDataStructure = (terminals) => {
	if (!isChangingExistingSubscription(terminals)) return undefined;

	const baseTerminal = terminals?.find((x) => x.type === "hardware");
	const changeSubscription = baseTerminal?.subItems?.find((item) => item?.type === "msisdn");
	const msisdn = removeCountryCode(changeSubscription?.metadata?.msisdnDetails?.msisdn);
	const selectedSubscriptionExceptNew = baseTerminal?.subItems?.find(
		(item) => item?.type === "subscription" && !item?.metadata?.existingSubscription,
	);
	const subscriptionProductId = selectedSubscriptionExceptNew?.data?.id;
	const addonProductsToAdd = selectedSubscriptionExceptNew?.subItems?.filter((subItem) => !subItem?.removed);
	const addonProductsToRemove = selectedSubscriptionExceptNew?.subItems?.filter((subItem) => subItem?.removed);

	return [
		pickBy({
			msisdn,
			addonProductsToAdd,
			addonProductsToRemove,
			subscriptionProductId,
			devices: getDevices(terminals),
			waitForEquipment: true,
		}),
	];
};

export const isAccessory = (item) => {
	const categoriesCharacterizedAsAccessory = [ProductCategory.SOUND, ProductCategory.ACCESSORY, ProductCategory.ROUTER];
	return item?.type === "hardware" && categoriesCharacterizedAsAccessory.includes(item?.metadata?.category);
};

const isSendingTerminalOnSubscriptionOrder = (terminals) => {
	return (
		isOrderingNewSubscription(terminals) ||
		isImportingSubscription(terminals) ||
		isChangingExistingSubscription(terminals)
	);
};

const isChangingExistingSubscription = (terminals) => {
	return terminals?.some((terminal) =>
		terminal?.subItems.some(
			(item) =>
				item?.type === "msisdn" &&
				item?.metadata?.msisdnDetails?.action === "KEEP_NUMBER" &&
				item?.metadata?.msisdnDetails?.accountId,
		),
	);
};

const isOrderingNewSubscription = (terminals) => {
	const notChangingOrImportingSubscription = !(
		isChangingExistingSubscription(terminals) || isImportingSubscription(terminals)
	);
	return (
		notChangingOrImportingSubscription &&
		terminals?.some(
			(terminal) =>
				terminal?.subItems.some((item) => item?.type === "subscription") || terminal?.type === "subscription",
		)
	);
};

const isImportingSubscription = (terminals) => {
	return terminals?.some((terminal) =>
		terminal?.subItems.some(
			(item) =>
				item?.type === "msisdn" &&
				item?.metadata?.msisdnDetails?.action === "KEEP_NUMBER" &&
				!item?.metadata?.msisdnDetails?.accountId,
		),
	);
};

export const oneTimePriceIsZero = (terminals) => {
	const sendingTerminalOnSubscriptionOrder = isSendingTerminalOnSubscriptionOrder(terminals);
	const standaloneProducts = getStandaloneProducts(terminals, sendingTerminalOnSubscriptionOrder);
	const phoneOrderedWithSubscription = getDevices(terminals);
	const getPriceExceptForSwap = (item) => (item?.swapProductId || item?.swapOrderId ? 0 : item?.price);
	return (
		[...phoneOrderedWithSubscription, ...standaloneProducts]?.reduce(
			(acc, curr) => acc + getPriceExceptForSwap(curr),
			0,
		) === 0
	);
};

const getPersonals = (contactDetails) => {
	const phoneNumber = contactDetails?.data?.phoneNumber?.replace(/\s/g, "");
	const phoneNumberWithCountryCode = phoneNumber?.length === 8 ? `47${phoneNumber}` : phoneNumber;
	return {
		email: contactDetails?.data?.email,
		phoneNumber: phoneNumberWithCountryCode,
	};
};

const getHardwareOrder = (terminals, sendingTerminalOnSubscriptionOrder, payment) => {
	const onlyAccessoryProducts = terminals?.length && !terminals?.some((item) => isEligibleForSubscription(item));
	const hasAccessories =
		terminals?.find((item) => item?.type === ShoppingCartItemEnum.HARDWARE)?.subItems?.some(isAccessory) ||
		onlyAccessoryProducts;
	const standaloneProducts =
		sendingTerminalOnSubscriptionOrder && !hasAccessories
			? undefined
			: getStandaloneProducts(terminals, sendingTerminalOnSubscriptionOrder);
	const consent = terminals?.find((item) => item?.type === ShoppingCartItemEnum.HARDWARE)?.metadata?.consent || null;
	return pickBy({
		standaloneProducts,
		delivery: {
			addressId: terminals?.find((item) => item?.type === ShoppingCartItemEnum.HARDWARE)?.metadata?.deliveryDetails
				.addressId,
			deliveryService: terminals?.find((item) => item?.type === ShoppingCartItemEnum.HARDWARE)?.metadata
				?.deliveryDetails.deliveryService,
			deliveryToken:
				terminals?.find((item) => item?.type === ShoppingCartItemEnum.HARDWARE)?.metadata?.deliveryDetails
					.deliveryService === "PORTERBUDDY"
					? terminals?.find((item) => item?.type === ShoppingCartItemEnum.HARDWARE)?.metadata?.deliveryDetails
							.deliveryWindow?.token
					: undefined,
		},
		payment,
		consent,
	});
};

const getPayment = (paymentMethod, amountToPay, paymentRedirectUrls, contactDetails, orderReferenceId = null) => {
	if (!amountToPay) return { registerPayment: false, method: { type: "INVOICE" } };

	return {
		registerPayment: true,
		purchaseDescription: "Kjøp av mobil",
		orderReferenceId: orderReferenceId || uuid().replace(/-/g, "").slice(0, 20),
		amount: amountToPay,
		successUrl: paymentRedirectUrls?.successUrl,
		cancelUrl: paymentRedirectUrls?.cancelUrl,
		method: {
			type: paymentMethod || "CARD",
		},
		phoneNumber: `+${contactDetails.phoneNumber}`,
		voucherCode: null,
	};
};

const mockedPersonals = {
	email: "test@test.no",
	phoneNumber: "4799999999",
};

export const getOrderStructure = (
	{ terminals, paymentMethod = "CARD", amountToPay, paymentRedirectUrls, orderReferenceId = null },
	mockPersonals = false,
) => {
	const purchasableItems = terminals?.filter((item) => item.type !== "contact-information");
	const newSubscriptions = getNewSubscriptionDataStructure(purchasableItems);
	const importSubscriptions = getImportSubscriptionDataStructure(purchasableItems);
	const changeSubscriptions = getChangeSubscriptionDataStructure(purchasableItems);
	const personals = mockPersonals
		? mockedPersonals
		: getPersonals(terminals?.find((item) => item.type === "contact-information"));

	const payment = getPayment(paymentMethod, amountToPay, paymentRedirectUrls, personals, orderReferenceId);

	const sendingTerminalOnSubscriptionOrder = newSubscriptions || importSubscriptions || changeSubscriptions;
	const hardwareOrder = getHardwareOrder(purchasableItems, sendingTerminalOnSubscriptionOrder, payment);

	return pickBy({
		hardwareOrder,
		creditEvaluation: { acceptManualCreditEvaluation: true },
		newSubscriptions,
		importSubscriptions,
		changeSubscriptions,
		personals,
	});
};
